/**
 * Property renames applied to mock data persisted in IndexedDB before them.
 *
 * With `doPersistMockData` on, a browser serves its saved copy of each list,
 * not mockData.ts -- so renaming a property in the seed data and the specs
 * leaves that copy on the old name, which nothing reads any more. Renaming it
 * on read keeps the records a developer has added, where Clear+Refresh would
 * throw them away.
 *
 * Keyed by the list's storageKey. An entry stays harmless once every copy has
 * been migrated: a row already on the new name is left alone.
 */
const RENAMES_BY_STORAGE_KEY: Record<string, Record<string, string>> = {
  // cynque-api-server #1781: one UTC startDateTime / endDateTime on both.
  calls: {
    callStartDateTime: 'startDateTime',
    callEndDateTime: 'endDateTime',
  },
  meetings: {
    meetingStartDateTime: 'startDateTime',
    meetingEndDateTime: 'endDateTime',
  },
  // cynque-api-server #1770: the activity email takes /api/emails' names.
  emails: {
    to: 'toAddresses',
    cc: 'ccAddresses',
    bcc: 'bccAddresses',
    emailSentDateTime: 'sentAt',
  },
}

/**
 * Renames `listData`'s legacy properties in place. Returns whether anything
 * changed, so the caller can write the migrated list back once.
 */
export function renameLegacyPersistedMockKeys(
  storageKey: string,
  listData: unknown
): boolean {
  const renames = RENAMES_BY_STORAGE_KEY[storageKey]
  if (!renames || !Array.isArray(listData)) {
    return false
  }
  let didChange = false
  for (const row of listData) {
    if (!row || typeof row !== 'object') {
      continue
    }
    for (const [from, to] of Object.entries(renames)) {
      if (!(from in row)) {
        continue
      }
      if (!(to in row) || row[to] == null) {
        row[to] = row[from]
      }
      delete row[from]
      didChange = true
    }
  }
  return didChange
}
